import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { getCroppedImg } from "@/utils/cropImage";

interface Props {
  imageSrc: string;
  /** Aspect ratio — 1 for avatar, 3 for banner */
  aspect: number;
  /** "round" for avatar, "rect" for banner */
  cropShape?: "round" | "rect";
  title?: string;
  onConfirm: (blob: Blob) => void;
  onClose: () => void;
}

export default function ImageCropperModal({
  imageSrc,
  aspect,
  cropShape = "rect",
  title = "Kırp & Düzenle",
  onConfirm,
  onClose,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, "image/jpeg", 0.92);
      onConfirm(blob);
    } catch {
      // ignore
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div>
            <h3 className="font-bold text-stone-900 text-base">{title}</h3>
            <p className="text-xs text-stone-400 mt-0.5">
              Sürükle, yakınlaştır veya uzaklaştır · Mavi çerçeve kırpma alanı
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-xl cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Crop area */}
        <div className="relative bg-stone-900 flex-1" style={{ minHeight: 300 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>

        {/* Zoom slider */}
        <div className="px-5 py-3 border-t border-stone-100 bg-stone-50">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-stone-400 flex-shrink-0">
              <i className="ri-subtract-line text-sm" />
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 accent-emerald-600 cursor-pointer"
            />
            <div className="w-5 h-5 flex items-center justify-center text-stone-400 flex-shrink-0">
              <i className="ri-add-line text-sm" />
            </div>
            <span className="text-xs text-stone-500 font-medium w-10 text-right flex-shrink-0">
              {zoom.toFixed(1)}x
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-stone-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing}
            className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <i className="ri-scissors-cut-line" />
                Uygula
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
